import { DOCUMENT, DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';
type Theme = 'light' | 'dark';

/**
 * localStorage key for the user's theme choice. Only saved while dark mode is allowed.
 * Keep in sync with the inline script in each app's index*.html, which applies the theme before first paint.
 */
export const themeStorageKey = 'themePreference';

/** Used until the user picks a theme */
const defaultThemePreference: ThemePreference = 'system';

const themeAttribute = 'data-bs-theme';

const isThemePreference = (value: unknown): value is ThemePreference => value === 'light' || value === 'dark' || value === 'system';

/**
 * Light / dark colour theme.
 *
 * Dark mode is behind the `allowDarkMode` flag in `assets/config.json`. Until `setDarkModeAllowed(true)` is called
 * (once the config has loaded), the app is always light and the theme menu is hidden.
 *
 * Sets `data-bs-theme` on `<html>`, which switches Bootstrap's colour mode and our colour tokens
 * (see `apps/shared/src/styles/_theme.scss`). The choice is saved per browser in localStorage.
 * Printing always uses the light theme.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly window = this.document.defaultView;
  private readonly darkSchemeQuery = this.window?.matchMedia?.('(prefers-color-scheme: dark)');
  private readonly systemPrefersDark = signal(this.darkSchemeQuery?.matches ?? false);

  /** undefined until the config has loaded */
  private readonly darkModeAllowedState = signal<boolean | undefined>(undefined);
  private isPrinting = false;

  /** Whether users can switch theme (allowDarkMode in assets/config.json) */
  public readonly isDarkModeAllowed = computed(() => this.darkModeAllowedState() === true);

  /** What the user picked */
  public readonly preference = signal<ThemePreference>(this.readStoredPreference());

  /** The theme actually shown */
  public readonly theme = computed<Theme>(() => {
    if (!this.isDarkModeAllowed()) {
      return 'light';
    }

    const preference = this.preference();

    if (preference === 'system') {
      return this.systemPrefersDark() ? 'dark' : 'light';
    }

    return preference;
  });

  public constructor() {
    let themeBeforePrint: string | null = null;

    const onSystemSchemeChange = (event: MediaQueryListEvent) => {
      this.systemPrefersDark.set(event.matches);
      this.applyTheme();
    };

    // Keep other open tabs in step
    const onStorage = (event: StorageEvent) => {
      if (event.key === themeStorageKey) {
        this.preference.set(isThemePreference(event.newValue) ? event.newValue : defaultThemePreference);
        this.applyTheme();
      }
    };

    const onBeforePrint = () => {
      themeBeforePrint = this.document.documentElement.getAttribute(themeAttribute);
      this.isPrinting = true;
      this.setDocumentTheme('light');
    };

    const onAfterPrint = () => {
      this.isPrinting = false;

      if (this.darkModeAllowedState() !== undefined) {
        this.applyTheme();
      } else if (themeBeforePrint) {
        // Config hasn't loaded yet - put back what the index.html script set
        this.document.documentElement.setAttribute(themeAttribute, themeBeforePrint);
      }
    };

    this.darkSchemeQuery?.addEventListener?.('change', onSystemSchemeChange);
    this.window?.addEventListener('storage', onStorage);
    this.window?.addEventListener('beforeprint', onBeforePrint);
    this.window?.addEventListener('afterprint', onAfterPrint);

    inject(DestroyRef).onDestroy(() => {
      this.darkSchemeQuery?.removeEventListener?.('change', onSystemSchemeChange);
      this.window?.removeEventListener('storage', onStorage);
      this.window?.removeEventListener('beforeprint', onBeforePrint);
      this.window?.removeEventListener('afterprint', onAfterPrint);
    });
  }

  /**
   * Call once the app config has loaded. When dark mode is not allowed the app is always light,
   * and any saved choice is cleared so the next page load starts light too.
   */
  public setDarkModeAllowed(allowed: boolean): void {
    this.darkModeAllowedState.set(allowed);

    if (allowed) {
      // Save even the default so the index.html script can apply it before first paint on the next load
      this.storePreference(this.preference());
    } else {
      this.clearStoredPreference();
    }

    this.applyTheme();
  }

  public setPreference(preference: ThemePreference): void {
    if (!this.isDarkModeAllowed()) {
      return;
    }

    this.preference.set(preference);
    this.storePreference(preference);
    this.applyTheme();
  }

  private applyTheme(): void {
    // Leave the theme set by the index.html script alone until we know whether dark mode is allowed.
    // While printing, stay light - afterprint applies the latest theme.
    if (this.darkModeAllowedState() === undefined || this.isPrinting) {
      return;
    }

    this.setDocumentTheme(this.theme());
  }

  private setDocumentTheme(theme: Theme): void {
    this.document.documentElement.setAttribute(themeAttribute, theme);
  }

  private readStoredPreference(): ThemePreference {
    try {
      const stored = localStorage.getItem(themeStorageKey);

      return isThemePreference(stored) ? stored : defaultThemePreference;
    } catch {
      // Storage can be blocked (e.g. privacy settings) - fall back to the default
      return defaultThemePreference;
    }
  }

  private storePreference(preference: ThemePreference): void {
    try {
      localStorage.setItem(themeStorageKey, preference);
    } catch {
      // Storage can be blocked - the theme still applies for this page view
    }
  }

  private clearStoredPreference(): void {
    try {
      localStorage.removeItem(themeStorageKey);
    } catch {
      // Storage can be blocked - nothing to clear
    }
  }
}
